import { useEffect, useRef, useState } from 'react';
import { SupportApi, EnquiryApi } from '../api';

// Agent face used when the admin hasn't uploaded an avatar.
function AgentFace({ uri, size = 40 }) {
  if (uri) return <img className="cw-face" src={uri} alt="" style={{ width: size, height: size }} />;
  return (
    <span className="cw-face cw-face-svg" style={{ width: size, height: size }}>
      <svg viewBox="0 0 24 24" width={size * 0.64} height={size * 0.64} aria-hidden="true">
        <circle cx="12" cy="8" r="4" fill="currentColor" />
        <path d="M4 20c0-4 4-6 8-6s8 2 8 6" fill="currentColor" />
      </svg>
    </span>
  );
}

let idSeq = 1;
const nextId = () => `m${idSeq++}`;

const ASK_CONTACT = 'Sure — could you share your name and number so our team can reach you?';

export default function ChatWidget() {
  const [bot, setBot] = useState(null);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  // Lead capture: 'idle' → 'asking' (form shown) → 'sent'.
  const [lead, setLead] = useState('idle');
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [leadErr, setLeadErr] = useState('');
  const [sending, setSending] = useState(false);
  const bodyRef = useRef(null);

  // The conversation so far, saved with the enquiry so the admin sees the
  // context. Kept in a ref because the send handler runs after a delay.
  const threadRef = useRef([]);
  threadRef.current = messages;

  useEffect(() => {
    let alive = true;
    SupportApi.bot()
      .then((r) => {
        if (!alive) return;
        const b = r.bot || {};
        setBot(b);
        if (b.welcome) setMessages([{ id: nextId(), from: 'bot', text: b.welcome }]);
      })
      .catch(() => alive && setBot({ enabled: false }));
    return () => { alive = false; };
  }, []);

  // Keep the newest message in view as the thread grows.
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open, lead]);

  const push = (msg) => setMessages((prev) => [...prev, { id: nextId(), ...msg }]);

  const askQuestion = (item) => {
    push({ from: 'user', text: item.q });
    setTimeout(() => push({ from: 'bot', text: item.a }), 350);
  };

  // A typed message can't be answered by the bot, so we promise a callback and
  // then collect the details needed to actually make it.
  const send = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput('');
    push({ from: 'user', text });

    setTimeout(() => {
      if (bot?.freeTextReply) push({ from: 'bot', text: bot.freeTextReply });
      if (lead === 'idle') {
        push({ from: 'bot', text: ASK_CONTACT });
        setLead('asking');
      }
    }, 350);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const phone = form.phone.replace(/\D/g, '');
    if (!name) return setLeadErr('Please enter your name.');
    if (phone.length < 10) return setLeadErr('Please enter a valid phone number.');

    setLeadErr('');
    setSending(true);
    try {
      // The visitor's own words make the most useful summary line.
      const firstAsk = threadRef.current.find((m) => m.from === 'user')?.text || '';
      await EnquiryApi.create({
        name,
        phone,
        email: form.email.trim(),
        subject: 'Support chat',
        message: firstAsk,
        source: 'chatbot',
        transcript: threadRef.current.map((m) => ({ from: m.from, text: m.text })),
      });
      setLead('sent');
      push({ from: 'bot', text: `Thanks ${name}! Our team will contact you on ${phone} shortly.` });
    } catch (err) {
      setLeadErr(err.message || 'Could not send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const openWhatsApp = () => {
    const num = bot?.whatsapp?.number;
    if (!num) return;
    const msg = encodeURIComponent(bot.whatsapp.message || '');
    window.open(`https://wa.me/${num}${msg ? `?text=${msg}` : ''}`, '_blank', 'noopener');
  };

  if (!bot || !bot.enabled) return null;

  const questions = bot.questions || [];
  const hasWhatsApp = Boolean(bot.whatsapp?.number);

  return (
    <>
      {open ? (
        <div className="cw-panel" role="dialog" aria-label="Support chat">
          <div className="cw-head">
            <AgentFace uri={bot.agentAvatar} size={40} />
            <div className="cw-head-text">
              <strong>{bot.agentName || 'Support'}</strong>
              <span className="cw-online"><i /> Online</span>
            </div>
            <button className="cw-close" onClick={() => setOpen(false)} aria-label="Close chat">×</button>
          </div>

          <div className="cw-body" ref={bodyRef}>
            {messages.map((m) =>
              m.from === 'bot' ? (
                <div className="cw-row cw-row-bot" key={m.id}>
                  <AgentFace uri={bot.agentAvatar} size={28} />
                  <p className="cw-bubble cw-bubble-bot">{m.text}</p>
                </div>
              ) : (
                <div className="cw-row cw-row-user" key={m.id}>
                  <p className="cw-bubble cw-bubble-user">{m.text}</p>
                </div>
              )
            )}

            {lead === 'asking' ? (
              <form className="cw-lead" onSubmit={submitLead}>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  aria-label="Your name"
                  autoFocus
                />
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 12) })}
                  placeholder="Phone number"
                  inputMode="numeric"
                  aria-label="Phone number"
                />
                <input
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email (optional)"
                  type="email"
                  aria-label="Email"
                />
                {leadErr ? <span className="cw-lead-err">{leadErr}</span> : null}
                <button type="submit" disabled={sending}>
                  {sending ? 'Sending…' : 'Share my details'}
                </button>
              </form>
            ) : null}

            {questions.length ? (
              <div className="cw-chips">
                <span className="cw-chips-label">Quick questions</span>
                {questions.map((q, i) => (
                  <button className="cw-chip" key={i} onClick={() => askQuestion(q)}>
                    <span>{q.q}</span>
                    <i>›</i>
                  </button>
                ))}
              </div>
            ) : null}

            {hasWhatsApp ? (
              <button className="cw-wa" onClick={openWhatsApp}>
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path
                    d="M12 2a10 10 0 0 0-8.6 15l-1.3 4.7 4.8-1.3A10 10 0 1 0 12 2zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .3-3.4-.7-2.9-1.2-4.7-4.1-4.9-4.3-.1-.2-1.1-1.5-1.1-2.8 0-1.3.7-2 .9-2.2.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 2c.1.2.1.4 0 .5l-.4.5c-.2.2-.3.4-.1.7.2.3.9 1.4 1.9 2.3 1.3 1.1 2.3 1.5 2.6 1.6.3.1.4.1.6-.1l.8-1c.2-.2.4-.2.6-.1l1.9.9c.3.1.4.2.5.3.1.2.1.7-.1 1.3z"
                    fill="#fff"
                  />
                </svg>
                {bot.whatsapp.label || 'WhatsApp us'}
              </button>
            ) : null}
          </div>

          <form className="cw-composer" onSubmit={send}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message…"
              aria-label="Message"
            />
            <button type="submit" disabled={!input.trim()} aria-label="Send">
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </form>
        </div>
      ) : null}

      <button
        className={`cw-launcher${open ? ' is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close support chat' : 'Chat with support'}
      >
        {open ? (
          <span className="cw-launcher-x">×</span>
        ) : (
          <>
            <AgentFace uri={bot.agentAvatar} size={54} />
            <span className="cw-ping" />
          </>
        )}
      </button>
    </>
  );
}
